import { observer } from 'mobx-react'
import Papa from 'papaparse'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import { EmptyState } from '../../components/DashboardPrimitives'
import { DropdownContext, DropdownItem } from '../../components/Dropdown'
import Modal, { ModalFooter } from '../../components/Modal'
import Radom, { CreateCryptoPayoutEntity } from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { getMethod, unifiedAssetNetworkOptions } from '../../util/Managed'
import { ROUTES, errorToast, floorAssetAmount, formatAssetAmount, shortAddress, successToast, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import EscapableDropdown from './EscapableDropdown'
import RecipientAvatar from './RecipientAvatar'
import {
  BlockerNotice,
  QuoteBox,
  QuoteLabel,
  QuoteRow,
  QuoteValue,
  SectionLabel,
  SelectionLabel,
  SelectionMeta,
  SelectionSubtext,
  SimpleMethodIcon,
  focusRing
} from './SendStyles'
import { IBAN_REGEX, addressMatchesNetwork, normalizeOneOffInput } from './sendUtil'

// The mass-payout rail currently supports these networks and assets; the batch
// tab mirrors the same constraint as the existing mass payout sheet.
const BATCH_PROD_NETWORKS = ['Ethereum', 'Tron']
const BATCH_TEST_NETWORKS = ['SepoliaTestnet']
const BATCH_TICKERS = ['ETH', 'USDT', 'USDC', 'TRX']

interface BatchAsset {
  network: string
  token?: string
  maxAmount: string
}

interface BatchRow {
  id: string
  name: string
  email: string
  destination: string
  amount: string
  note: string
}

/** Per-cell validation result; note is display-only and never validated. */
interface RowIssues {
  destination?: string
  amount?: string
  email?: string
}

interface SavedBatchList {
  id: string
  name: string
  savedAt: number
  rows: Array<Pick<BatchRow, 'name' | 'email' | 'destination' | 'amount' | 'note'>>
}

const batchAssetKey = (asset: BatchAsset): string => `${asset.network}:${asset.token ?? 'native'}`

const newRowId = (): string => crypto.randomUUID()

const emptyRow = (): BatchRow => ({ id: newRowId(), name: '', email: '', destination: '', amount: '', note: '' })

// ---------------------------------------------------------------------------
// Saved lists — this-device-only payroll lists in localStorage. Storage can be
// unavailable (private mode, quotas), so every access is wrapped.
// ---------------------------------------------------------------------------

const BATCH_LISTS_STORAGE_KEY = 'radom.send.batchLists.v1'

const readSavedLists = (): SavedBatchList[] => {
  try {
    const raw = window.localStorage.getItem(BATCH_LISTS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry): entry is SavedBatchList =>
      !!entry && typeof entry.name === 'string' && Array.isArray(entry.rows))
  } catch (err) {
    console.error('Failed to read saved batch lists', err)
    return []
  }
}

const writeSavedLists = (lists: SavedBatchList[]): boolean => {
  try {
    window.localStorage.setItem(BATCH_LISTS_STORAGE_KEY, JSON.stringify(lists))
    return true
  } catch (err) {
    console.error('Failed to save batch lists', err)
    return false
  }
}

const TEMPLATE_CSV = [
  'destination,amount,name,email',
  '0x1f9090aaE28b8a3dCeaDf281B0F12828e676c326,150,Alice Example,alice@example.com',
  '0x8ba1f109551bD432803012645Ac136ddd64DBA72,75.5,Bob Example,bob@example.com'
].join('\n') + '\n'

const TEMPLATE_HREF = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE_CSV)}`

// ---------------------------------------------------------------------------
// Workbench styles
// ---------------------------------------------------------------------------

const WorkbenchScroller = styled.div`
  overflow-x: auto;
`

const rowIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-3px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`

const WorkbenchTable = styled.table`
  border-collapse: collapse;
  min-width: 720px;
  width: 100%;

  tbody tr {
    animation: ${rowIn} 0.18s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    tbody tr {
      animation: none;
    }
  }

  th {
    background: var(--surface-muted, #f8fafc);
    color: var(--text-muted, #6f7888);
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.03em;
    padding: 8px 12px;
    text-align: left;
    text-transform: uppercase;
    white-space: nowrap;

    &:first-child {
      border-radius: 10px 0 0 10px;
    }

    &:last-child {
      border-radius: 0 10px 10px 0;
    }
  }

  td {
    border-bottom: 1px solid var(--hairline-soft, #f1f4f9);
    padding: 6px 4px;
    vertical-align: top;
  }

  td:first-child {
    color: var(--text-disabled, #a4aab5);
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    padding: 14px 4px 0 12px;
    white-space: nowrap;
  }
`

const CellInput = styled.input<{ $invalid?: boolean, $mono?: boolean, $align?: 'right' }>`
  background: transparent;
  border: 1px solid ${({ $invalid }) => $invalid ? '#e5484d' : 'transparent'};
  border-radius: 8px;
  box-sizing: border-box;
  color: var(--ink, #20242d);
  font-family: ${({ $mono }) => $mono ? 'var(--font-mono, monospace)' : 'var(--font-sans)'};
  font-size: ${({ $mono }) => $mono ? '12px' : '13px'};
  font-variant-numeric: tabular-nums;
  min-height: 34px;
  outline: none;
  padding: 6px 8px;
  text-align: ${({ $align }) => $align ?? 'left'};
  transition: border-color 0.16s ease, background-color 0.16s ease;
  width: 100%;

  &:hover {
    background: var(--surface-hover, #f5f7fa);
  }

  &:focus {
    background: var(--surface, #ffffff);
    border-color: ${({ $invalid }) => $invalid ? '#e5484d' : 'rgba(17, 17, 17, 0.22)'};
    box-shadow: 0 0 0 3px ${({ $invalid }) => $invalid ? 'rgba(229, 72, 77, 0.12)' : 'rgba(17, 17, 17, 0.05)'};
  }

  &::placeholder {
    color: var(--text-disabled, #a4aab5);
  }
`

const AmountCell = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`

const AmountSuffix = styled.span`
  color: var(--text-faint, #8a919e);
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  padding-right: 4px;
`

const CellError = styled.span`
  color: #b42318;
  display: block;
  font-size: 11.75px;
  line-height: 1.4;
  padding: 3px 8px 2px;
`

const RemoveRowButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--text-disabled, #a4aab5);
  cursor: pointer;
  display: flex;
  height: 34px;
  justify-content: center;
  transition: background-color 0.16s ease, color 0.16s ease;
  width: 34px;

  &:hover {
    background: #fbeaea;
    color: #b42318;
  }

  ${focusRing}
`

const AddRowButton = styled.button`
  background: transparent;
  border: 1px dashed var(--border-strong, #d9dee8);
  border-radius: 10px;
  color: var(--text-muted, #556072);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  min-height: 38px;
  padding: 0 16px;
  transition: background-color 0.16s ease, border-color 0.16s ease;
  width: 100%;

  &:hover {
    background: var(--surface-muted, #f8fafc);
    border-color: var(--border-strong, #bfc6d2);
  }

  ${focusRing}
`

const DropZone = styled.div<{ $active?: boolean }>`
  align-items: center;
  background: ${({ $active }) => $active ? '#f2f6ff' : 'var(--surface-subtle, #fbfcfe)'};
  border: 1.5px dashed ${({ $active }) => $active ? '#7e93c9' : 'var(--border-strong, #d9dee8)'};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-height: 200px;
  padding: 28px 20px;
  text-align: center;
  transition: background-color 0.16s ease, border-color 0.16s ease;
`

const DropZoneTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 14.5px;
  font-weight: 550;
  line-height: 1.3;
`

const DropZoneSub = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12.5px;
  line-height: 1.45;
`

const DropZoneActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  padding-top: 10px;
`

const TotalsBar = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e9edf4);
  border-radius: 14px;
  bottom: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
  padding: 12px 16px;
  position: sticky;
  z-index: 5;
`

const TotalsSummary = styled.div`
  color: var(--text-muted, #6b7280);
  display: flex;
  flex-wrap: wrap;
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  gap: 8px;
  line-height: 1.4;

  strong {
    color: var(--ink, #20242d);
    font-weight: 550;
  }
`

// Downloads are actions, so this anchor dresses as a quiet secondary button
// rather than an inline link.
const TextLink = styled.a`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border-strong, #d6dae2);
  border-radius: 12px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 465;
  line-height: 1.2;
  min-height: 38px;
  padding: 0 14px;
  text-decoration: none;
  transition: background-color 0.16s ease, border-color 0.16s ease, transform 0.12s ease;

  &:hover {
    background: var(--surface-muted, #f8fafc);
    border-color: var(--border-strong, #c4cad6);
  }

  &:active {
    transform: translateY(1px);
  }

  ${focusRing}
`

const ToolbarNote = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 12px;
  line-height: 1.5;
`

const CrossMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='14' height='14' aria-hidden='true'>
    <path d='M6 6l12 12M18 6L6 18' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
  </svg>
)

const rowIssues = (row: BatchRow, asset?: BatchAsset): RowIssues => {
  const issues: RowIssues = {}
  const destination = normalizeOneOffInput(row.destination)
  const networkLabel = asset ? (asset.network === 'SepoliaTestnet' ? 'Sepolia' : asset.network) : 'the selected network'

  if (!destination) {
    issues.destination = 'Missing destination'
  } else if (IBAN_REGEX.test(destination)) {
    issues.destination = 'IBAN rows can’t be batched yet. Send bank transfers one at a time from Send funds'
  } else if (asset &&
    !addressMatchesNetwork(destination, asset.network, getMethod(asset.network, asset.token).chainId)) {
    issues.destination = `Not a valid ${networkLabel} address`
  }

  const amount = Number(row.amount)
  if (!row.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
    issues.amount = 'Amount must be greater than 0'
  }

  // The payout API mints a recipient record from the email, so every row
  // needs one (same contract the legacy mass-payout sheet satisfied).
  const email = row.email.trim()
  if (!email) {
    issues.email = 'Email required so the payout is attributed to this recipient'
  } else if (!/^\S+@\S+\.\S+$/.test(email)) {
    issues.email = 'Not a valid email'
  }

  return issues
}

const BatchSend = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const { has } = usePermissions()
  const canWritePayouts = has('WritePayouts')

  const [selectedAssetKey, setSelectedAssetKey] = useState<string>()
  const [rows, setRows] = useState<BatchRow[]>([])
  const [touchedRows, setTouchedRows] = useState<Set<string>>(new Set())
  const [lastAddedRowId, setLastAddedRowId] = useState<string>()
  const [importedFileName, setImportedFileName] = useState('')
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [savedLists, setSavedLists] = useState<SavedBatchList[]>(() => readSavedLists())
  const [isDragActive, setIsDragActive] = useState(false)
  const [isNamingList, setIsNamingList] = useState(false)
  const [listName, setListName] = useState('')
  const uploadInputRef = useRef<HTMLInputElement | null>(null)

  const unifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true

  const batchAssets = useMemo((): BatchAsset[] => {
    const supportedNetworks = testMode ? BATCH_TEST_NETWORKS : BATCH_PROD_NETWORKS
    const expanded = Treasury.managedBalances
      .filter(b => Number(b.balance) > 0 && !b.treasuryId)
      .flatMap((b): BatchAsset[] => {
        // Unified-treasury rows carry an asset ticker and no network; expand
        // one row into the batch-supported networks that settle the asset.
        // Every option carries the full unified balance as maxAmount; the
        // backend enforces the real spendable cap when the batch executes.
        if (b.asset) {
          if (testMode || !BATCH_TICKERS.includes(b.asset)) return []
          return unifiedAssetNetworkOptions(b.asset)
            .filter(option => supportedNetworks.includes(option.network))
            .map(option => ({ ...option, maxAmount: b.balance }))
        }
        if (b.network === undefined || !supportedNetworks.includes(b.network)) return []
        const method = getMethod(b.network, b.token)
        if (!BATCH_TICKERS.includes(method.ticker)) return []
        if (testMode ? !method.isTestnet : !!method.isTestnet) return []
        return [{ network: b.network, token: b.token, maxAmount: b.balance }]
      })
      .sort((a, b) => Number(b.maxAmount) - Number(a.maxAmount))

    if (!unifiedTreasury) return expanded
    return expanded.filter((asset, index, all) => {
      const ticker = getMethod(asset.network, asset.token).ticker
      return all.findIndex(other => getMethod(other.network, other.token).ticker === ticker) === index
    })
  }, [Treasury.managedBalances, testMode, unifiedTreasury])

  const selectedAsset = batchAssets.find(asset => batchAssetKey(asset) === selectedAssetKey)

  useEffect(() => {
    if (!selectedAsset && batchAssets.length > 0) {
      setSelectedAssetKey(batchAssetKey(batchAssets[0]))
    }
  }, [batchAssets, selectedAsset])

  const validation = useMemo(() => rows.map(row => ({
    row,
    issues: rowIssues(row, selectedAsset)
  })), [rows, selectedAsset])

  const invalidCount = validation.filter(v => v.issues.destination || v.issues.amount || v.issues.email).length
  const totalAmount = validation
    .filter(v => !v.issues.destination && !v.issues.amount && !v.issues.email)
    .reduce((sum, v) => sum + Number(v.row.amount), 0)
  const exceedsBalance = !!selectedAsset && totalAmount > Number(selectedAsset.maxAmount)
  const selectedMethod = selectedAsset ? getMethod(selectedAsset.network, selectedAsset.token) : undefined
  const balanceAfter = selectedAsset ? Number(selectedAsset.maxAmount) - totalAmount : undefined

  const updateRow = (id: string, patch: Partial<Omit<BatchRow, 'id'>>): void => {
    setRows(current => current.map(row => row.id === id ? { ...row, ...patch } : row))
  }

  const markRowTouched = (id: string): void => {
    setTouchedRows(current => {
      if (current.has(id)) return current
      const next = new Set(current)
      next.add(id)
      return next
    })
  }

  const addRow = (): void => {
    const row = emptyRow()
    setRows(current => [...current, row])
    setLastAddedRowId(row.id)
  }

  const removeRow = (id: string): void => {
    setRows(current => current.filter(row => row.id !== id))
  }

  const clearRows = (): void => {
    setRows([])
    setTouchedRows(new Set())
    setImportedFileName('')
  }

  const parseCsv = (file: File): void => {
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (results) => {
        const rawRows = (results.data || []).filter(cells =>
          Array.isArray(cells) && cells.some(cell => (cell ?? '').trim().length > 0))

        if (rawRows.length === 0) {
          errorToast('No rows found in the spreadsheet')
          return
        }

        // Accept an optional header row: destination,amount[,name]. Anything
        // whose first cell says "destination"/"address" is treated as a header.
        const [first, ...rest] = rawRows
        const firstCell = (first[0] ?? '').trim().toLowerCase()
        const hasHeader = ['destination', 'address', 'payout_address', 'wallet', 'wallet_address', 'recipient', 'to']
          .includes(firstCell)
        const dataRows = hasHeader ? rest : rawRows

        if (dataRows.length === 0) {
          errorToast('The spreadsheet only contains a header row')
          return
        }

        const imported: BatchRow[] = dataRows.map(cells => ({
          id: newRowId(),
          destination: (cells[0] ?? '').trim(),
          amount: (cells[1] ?? '').trim(),
          name: (cells[2] ?? '').trim(),
          email: (cells[3] ?? '').trim(),
          note: ''
        }))

        // Imports merge into the working table rather than replacing it, so a
        // hand-built list can be topped up from a spreadsheet.
        setRows(current => [...current, ...imported])
        setImportedFileName(file.name)
        successToast(`Imported ${dataRows.length} row(s)`)
      },
      error: (error) => {
        console.error('CSV parse error:', error)
        errorToast('Failed to parse spreadsheet')
      }
    })
  }

  const onUploadChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return
    parseCsv(file)
    event.target.value = ''
  }

  const submitBatch = async (): Promise<void> => {
    if (!selectedAsset || rows.length === 0 || invalidCount > 0) return

    setIsSubmitting(true)
    try {
      const payouts: CreateCryptoPayoutEntity[] = rows.map(row => ({
        gateway: 'crypto' as const,
        amount: row.amount.trim(),
        payoutAddress: normalizeOneOffInput(row.destination),
        network: selectedAsset.network,
        token: selectedAsset.token,
        name: row.name.trim() || undefined,
        email: row.email.trim()
      }))

      await Radom.createPayout({
        deductFees: false,
        sendEmailReceipts: false,
        payouts
      })

      successToast('Batch payout created. Review and confirm it from your payout history')
      setIsReviewOpen(false)
      clearRows()
      navigate(ROUTES.PAYOUTS)
    } catch (err: any) {
      errorToast(err?.response?.data?.error || err?.message || 'Failed to create batch payout')
    } finally {
      setIsSubmitting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Saved lists
  // -------------------------------------------------------------------------

  const persistLists = (lists: SavedBatchList[]): void => {
    setSavedLists(lists)
    if (!writeSavedLists(lists)) {
      errorToast('Couldn’t save on this device. Browser storage is unavailable')
    }
  }

  const defaultListName = (): string => importedFileName.replace(/\.csv$/i, '') ||
    `Payroll ${new Date().toLocaleDateString()}`

  const saveCurrentList = (): void => {
    const name = listName.trim()
    if (!name || rows.length === 0) return

    const entry: SavedBatchList = {
      id: newRowId(),
      name,
      savedAt: Date.now(),
      rows: rows.map(({ name: rowName, email, destination, amount, note }) => ({
        name: rowName,
        email,
        destination,
        amount,
        note
      }))
    }
    const existing = savedLists.find(list => list.name.toLowerCase() === name.toLowerCase())
    const next = existing
      ? savedLists.map(list => list.id === existing.id ? { ...entry, id: existing.id } : list)
      : [entry, ...savedLists]
    persistLists(next)
    setIsNamingList(false)
    setListName('')
    successToast(existing ? `Updated “${name}” on this device` : `Saved “${name}” on this device`)
  }

  const loadList = (list: SavedBatchList): void => {
    setRows(list.rows.map(row => ({
      id: newRowId(),
      name: row.name ?? '',
      email: row.email ?? '',
      destination: row.destination ?? '',
      amount: row.amount ?? '',
      note: row.note ?? ''
    })))
    setTouchedRows(new Set())
    setImportedFileName('')
    successToast(`Loaded “${list.name}” (${list.rows.length} recipient${list.rows.length === 1 ? '' : 's'})`)
  }

  const deleteList = (id: string): void => {
    persistLists(savedLists.filter(list => list.id !== id))
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  const renderAssetOption = (asset?: BatchAsset): ReactElement => {
    if (!asset) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select asset</span>
    }

    const method = getMethod(asset.network, asset.token)
    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SimpleMethodIcon $logo={method.logo} $size={20} />
      <SelectionMeta>
        <SelectionLabel>
          {unifiedTreasury ? method.ticker : `${method.ticker} on ${method.hostChain?.name ?? method.name}`}
        </SelectionLabel>
        <SelectionSubtext>
          Available {formatAssetAmount(floorAssetAmount(method.ticker, asset.maxAmount), method.ticker)} {method.ticker}
        </SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderSavedListsMenu = (): ReactElement => (
    <EscapableDropdown
      preferredAlignment='right'
      outerStyle={{ minHeight: 38, padding: '0 12px' }}
      selectedContent={<span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
        Saved lists{savedLists.length > 0 ? ` (${savedLists.length})` : ''}
      </span>}
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div style={{ minWidth: 300 }}>
            {savedLists.length === 0 && <div style={{
              color: 'var(--text-soft, #7d8492)',
              fontSize: 12.5,
              lineHeight: 1.5,
              padding: '14px 12px'
            }}>
              No saved lists yet. Build a table and use “Save list”.
            </div>}
            {savedLists.map(list => <DropdownItem
              key={list.id}
              style={{ cursor: 'default', padding: '10px 12px' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
                <SelectionMeta style={{ flexGrow: 1 }}>
                  <SelectionLabel style={{ fontSize: 13 }}>{list.name}</SelectionLabel>
                  <SelectionSubtext style={{ fontSize: 11.5 }}>
                    {list.rows.length} recipient{list.rows.length === 1 ? '' : 's'} · saved{' '}
                    {new Date(list.savedAt).toLocaleDateString()}
                  </SelectionSubtext>
                </SelectionMeta>
                <SecondaryButton
                  style={{ fontSize: 12, minHeight: 30, padding: '0 10px' }}
                  onClick={() => {
                    loadList(list)
                    closeDropdown()
                  }}>
                  Load
                </SecondaryButton>
                <SecondaryButton
                  aria-label={`Delete list ${list.name}`}
                  style={{ fontSize: 12, minHeight: 30, padding: '0 10px' }}
                  onClick={() => deleteList(list.id)}>
                  Delete
                </SecondaryButton>
              </div>
            </DropdownItem>)}
            <div style={{
              borderTop: '1px solid var(--surface-hover, #eef1f6)',
              color: 'var(--text-faint, #8a919e)',
              fontSize: 11.5,
              lineHeight: 1.5,
              padding: '8px 12px'
            }}>
              Lists are saved on this device only.
            </div>
          </div>}
        </DropdownContext.Consumer>
      } />
  )

  const renderTable = (): ReactElement => (
    <WorkbenchScroller>
      <WorkbenchTable>
        <thead>
          <tr>
            <th style={{ width: 28 }}>#</th>
            <th style={{ minWidth: 140, width: '16%' }}>Recipient</th>
            <th style={{ minWidth: 150, width: '18%' }}>Email</th>
            <th style={{ minWidth: 230 }}>Destination</th>
            <th style={{ minWidth: 130, width: 150 }}>Amount</th>
            <th style={{ minWidth: 110, width: '16%' }}>Note</th>
            <th style={{ width: 40 }}><span className='sr-only' style={{ display: 'none' }}>Remove</span></th>
          </tr>
        </thead>
        <tbody>
          {validation.map(({ row, issues }, index) => {
            const touched = touchedRows.has(row.id)
            const showDestinationError = !!issues.destination && (touched || row.destination.trim() !== '')
            const showAmountError = !!issues.amount && (touched || row.amount.trim() !== '')
            const showEmailError = !!issues.email && (touched || row.email.trim() !== '')

            return <tr key={row.id}>
              <td>{index + 1}</td>
              <td>
                <CellInput
                  value={row.name}
                  placeholder='Name'
                  autoFocus={row.id === lastAddedRowId}
                  aria-label={`Recipient name, row ${index + 1}`}
                  onBlur={() => markRowTouched(row.id)}
                  onChange={e => updateRow(row.id, { name: e.target.value })} />
              </td>
              <td>
                <CellInput
                  $invalid={showEmailError}
                  value={row.email}
                  type='email'
                  placeholder='name@company.com'
                  autoComplete='off'
                  spellCheck={false}
                  aria-label={`Recipient email, row ${index + 1}`}
                  aria-invalid={showEmailError}
                  onBlur={() => markRowTouched(row.id)}
                  onChange={e => updateRow(row.id, { email: e.target.value })} />
                {showEmailError && <CellError>{issues.email}</CellError>}
              </td>
              <td>
                <CellInput
                  $mono
                  $invalid={showDestinationError}
                  value={row.destination}
                  placeholder={selectedAsset?.network.startsWith('Tron') ? 'T… address' : '0x… address'}
                  spellCheck={false}
                  autoComplete='off'
                  aria-label={`Destination address, row ${index + 1}`}
                  aria-invalid={showDestinationError}
                  onBlur={() => markRowTouched(row.id)}
                  onChange={e => updateRow(row.id, { destination: e.target.value })} />
                {showDestinationError && <CellError>{issues.destination}</CellError>}
              </td>
              <td>
                <AmountCell>
                  <CellInput
                    $align='right'
                    $invalid={showAmountError}
                    value={row.amount}
                    inputMode='decimal'
                    autoComplete='off'
                    placeholder='0.00'
                    aria-label={`Amount, row ${index + 1}`}
                    aria-invalid={showAmountError}
                    onBlur={() => markRowTouched(row.id)}
                    onKeyDown={e => {
                      // Spreadsheet flow: Enter on the last row starts the next one.
                      if (e.key === 'Enter' && index === rows.length - 1) {
                        e.preventDefault()
                        addRow()
                      }
                    }}
                    onChange={e => {
                      if (/^\d*\.?\d*$/.test(e.target.value)) {
                        updateRow(row.id, { amount: e.target.value })
                      }
                    }} />
                  {selectedMethod && <AmountSuffix>{selectedMethod.ticker}</AmountSuffix>}
                </AmountCell>
                {showAmountError && <CellError>{issues.amount}</CellError>}
              </td>
              <td>
                <CellInput
                  value={row.note}
                  placeholder='Optional'
                  aria-label={`Note, row ${index + 1}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && index === rows.length - 1) {
                      e.preventDefault()
                      addRow()
                    }
                  }}
                  onChange={e => updateRow(row.id, { note: e.target.value })} />
              </td>
              <td>
                <RemoveRowButton
                  type='button'
                  aria-label={`Remove row ${index + 1}`}
                  onClick={() => removeRow(row.id)}>
                  <CrossMark />
                </RemoveRowButton>
              </td>
            </tr>
          })}
        </tbody>
      </WorkbenchTable>
    </WorkbenchScroller>
  )

  const sendBlocker = ((): string | undefined => {
    if (!canWritePayouts) {
      return 'Your role can’t create payouts. Ask an organization admin for the WritePayouts permission.'
    }
    if (batchAssets.length === 0) return undefined
    if (!selectedAsset) return 'Choose the asset to send first.'
    if (rows.length === 0) return undefined
    if (invalidCount > 0) return `Fix ${invalidCount} row${invalidCount === 1 ? '' : 's'} before sending.`
    if (exceedsBalance && selectedMethod) {
      return `Rows total ${formatAssetAmount(String(totalAmount), selectedMethod.ticker)} ${selectedMethod.ticker}, more than the available balance.`
    }
    return undefined
  })()

  const canReview = rows.length > 0 && invalidCount === 0 && !exceedsBalance && !!selectedAsset && canWritePayouts

  const renderReviewModal = (): ReactElement => (
    <Modal
      visible={isReviewOpen}
      onClose={() => setIsReviewOpen(false)}
      presentation='drawer'
      title={`Review batch · ${rows.length} recipient${rows.length === 1 ? '' : 's'}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 'min(560px, 100%)' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((row, index) => <div
            key={row.id}
            style={{
              alignItems: 'center',
              borderBottom: index === rows.length - 1 ? 'none' : '1px solid var(--hairline-soft, #f1f4f9)',
              display: 'flex',
              gap: 12,
              padding: '8px 2px'
            }}>
            <RecipientAvatar name={row.name.trim() || row.destination} size={28} />
            <SelectionMeta style={{ flexGrow: 1 }}>
              <SelectionLabel style={{ fontSize: 13 }}>
                {row.name.trim() || <span style={{ color: 'var(--text-disabled, #a4aab5)' }}>Unnamed recipient</span>}
              </SelectionLabel>
              <SelectionSubtext style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 11.5 }}>
                {shortAddress(normalizeOneOffInput(row.destination), 8)}
              </SelectionSubtext>
              {row.email.trim() && <SelectionSubtext style={{ fontSize: 11.5 }}>
                {row.email.trim()}
              </SelectionSubtext>}
              {row.note.trim() && <SelectionSubtext style={{ fontSize: 11.5 }}>
                {row.note.trim()}
              </SelectionSubtext>}
            </SelectionMeta>
            <span style={{
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}>
              {formatAssetAmount(row.amount, selectedMethod?.ticker)} {selectedMethod?.ticker}
            </span>
          </div>)}
        </div>

        <QuoteBox>
          <QuoteRow>
            <QuoteLabel>Recipients</QuoteLabel>
            <QuoteValue>{rows.length}</QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Total</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(String(totalAmount), selectedMethod?.ticker)} {selectedMethod?.ticker}
            </QuoteValue>
          </QuoteRow>
          {balanceAfter !== undefined && <QuoteRow>
            <QuoteLabel>Balance after</QuoteLabel>
            <QuoteValue style={{ color: balanceAfter < 0 ? '#b42318' : undefined }}>
              {formatAssetAmount(String(balanceAfter), selectedMethod?.ticker)} {selectedMethod?.ticker}
            </QuoteValue>
          </QuoteRow>}
        </QuoteBox>

        <ToolbarNote>
          The batch is created awaiting confirmation. Nothing moves until you confirm it from your
          payout history.
        </ToolbarNote>
      </div>
      <ModalFooter>
        <SecondaryButton onClick={() => setIsReviewOpen(false)}>Cancel</SecondaryButton>
        <PrimaryButton
          isLoading={isSubmitting}
          onClick={submitBatch}>
          Send {rows.length} payout{rows.length === 1 ? '' : 's'}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  )

  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionLabel>Asset to send</SectionLabel>
      {
        batchAssets.length > 0
          ? <EscapableDropdown
            matchTriggerWidth
            outerStyle={{ minHeight: 44 }}
            selectedContent={renderAssetOption(selectedAsset)}
            dropdownContent={
              <DropdownContext.Consumer>
                {({ closeDropdown }) => <div>
                  {batchAssets.map(asset => <DropdownItem
                    key={batchAssetKey(asset)}
                    style={{ padding: '11px 12px' }}
                    onClick={() => {
                      setSelectedAssetKey(batchAssetKey(asset))
                      closeDropdown()
                    }}>
                    {renderAssetOption(asset)}
                  </DropdownItem>)}
                </div>}
              </DropdownContext.Consumer>
            } />
          : <EmptyState style={{ minHeight: 84 }}>
            Batch sending currently pays out on Ethereum and Tron. Deposit ETH, USDC, USDT or TRX first
            from Add funds.
          </EmptyState>
      }
    </div>

    <input
      ref={uploadInputRef}
      type='file'
      accept='.csv'
      style={{ display: 'none' }}
      onChange={onUploadChange} />

    {rows.length === 0
      ? <DropZone
        $active={isDragActive}
        onDragOver={e => {
          e.preventDefault()
          setIsDragActive(true)
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={e => {
          e.preventDefault()
          setIsDragActive(false)
          const file = e.dataTransfer.files?.[0]
          if (file) parseCsv(file)
        }}>
        <DropZoneTitle>Drag a CSV here to build your batch</DropZoneTitle>
        <DropZoneSub>
          Columns: destination, amount, name, email. Header row optional,
          amounts in the selected asset.
        </DropZoneSub>
        <DropZoneActions>
          <SecondaryButton
            style={{ fontSize: 13, minHeight: 38 }}
            onClick={() => uploadInputRef.current?.click()}>
            Browse files
          </SecondaryButton>
          <SecondaryButton style={{ fontSize: 13, minHeight: 38 }} onClick={addRow}>
            + Add by hand
          </SecondaryButton>
          {savedLists.length > 0 && renderSavedListsMenu()}
          <TextLink href={TEMPLATE_HREF} download='batch-recipients-template.csv'>
            Template
          </TextLink>
        </DropZoneActions>
      </DropZone>
      : <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SecondaryButton
              style={{ fontSize: 13, minHeight: 38 }}
              onClick={() => uploadInputRef.current?.click()}>
              Import CSV
            </SecondaryButton>
            <TextLink href={TEMPLATE_HREF} download='batch-recipients-template.csv'>
              Template
            </TextLink>
            {importedFileName && <ToolbarNote>{importedFileName}</ToolbarNote>}
            <div style={{ flexGrow: 1 }} />
            {renderSavedListsMenu()}
            <SecondaryButton
              style={{ fontSize: 13, minHeight: 38 }}
              onClick={() => {
                setListName(current => current || defaultListName())
                setIsNamingList(naming => !naming)
              }}>
              Save list
            </SecondaryButton>
          </div>
          {isNamingList && <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <CellInput
              style={{ background: 'var(--surface, #ffffff)', border: '1px solid var(--border-strong, #d8dde7)', maxWidth: 280 }}
              value={listName}
              autoFocus
              placeholder='List name'
              aria-label='Saved list name'
              onKeyDown={e => {
                if (e.key === 'Enter') saveCurrentList()
                if (e.key === 'Escape') setIsNamingList(false)
              }}
              onChange={e => setListName(e.target.value)} />
            <PrimaryButton
              style={{ fontSize: 13, minHeight: 38 }}
              disabled={!listName.trim() || rows.length === 0}
              onClick={saveCurrentList}>
              Save on this device
            </PrimaryButton>
            <SecondaryButton
              style={{ fontSize: 13, minHeight: 38 }}
              onClick={() => setIsNamingList(false)}>
              Cancel
            </SecondaryButton>
          </div>}
        </div>
        {renderTable()}
        <AddRowButton type='button' onClick={addRow}>+ Add recipient</AddRowButton>

        <TotalsBar>
          <TotalsSummary>
            <span><strong>{rows.length}</strong> recipient{rows.length === 1 ? '' : 's'}</span>
            {selectedMethod && <>
              <span aria-hidden='true'>·</span>
              <span>
                Total <strong>
                  {formatAssetAmount(String(totalAmount), selectedMethod.ticker)} {selectedMethod.ticker}
                </strong>
              </span>
            </>}
            {selectedMethod && balanceAfter !== undefined && <>
              <span aria-hidden='true'>·</span>
              <span style={{ color: balanceAfter < 0 ? '#b42318' : undefined }}>
                Balance after <strong style={{ color: balanceAfter < 0 ? '#b42318' : undefined }}>
                  {formatAssetAmount(String(balanceAfter), selectedMethod.ticker)} {selectedMethod.ticker}
                </strong>
              </span>
            </>}
          </TotalsSummary>
          <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
            <SecondaryButton
              style={{ fontSize: 13, minHeight: 38 }}
              onClick={clearRows}>
              Clear all
            </SecondaryButton>
            <PrimaryButton
              disabled={!canReview}
              style={{ minHeight: 38 }}
              onClick={() => setIsReviewOpen(true)}>
              Review & send
            </PrimaryButton>
          </div>
          {sendBlocker && <div style={{ flexBasis: '100%' }}>
            <BlockerNotice style={{ padding: '8px 12px' }}>{sendBlocker}</BlockerNotice>
          </div>}
        </TotalsBar>

        <ToolbarNote>
          Batches are created awaiting confirmation. Nothing moves until you confirm the payout
          from <Link to={ROUTES.PAYOUTS} style={{ color: 'var(--ink, #20242d)', textDecoration: 'underline', textUnderlineOffset: 3 }}>your payout history</Link>.
        </ToolbarNote>
      </>}

    {renderReviewModal()}
  </div>
})

export default BatchSend
